import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { LitigationReferralRepository } from '../../Core-Repositories/litigationReferral.repository';
import { TopLevelCompanyData } from 'src/app/Core-Repositories/actionView.repository';

@Injectable({ providedIn: 'root' })
export class ReferralsService {
    constructor(private readonly _litRefRepo: LitigationReferralRepository) {}

    async getCompanyLitigationReferrals(companyId: number): Promise<Core.LitigationReferralListModel[]> {
        return lastValueFrom(this._litRefRepo.getCompanyLitigationReferrals(companyId));
    }

    async getSiteLitigationReferrals(siteId: number): Promise<Core.LitigationReferralListModel[]> {
        return lastValueFrom(this._litRefRepo.getSiteLitigationReferrals(siteId));
    }

    async getShowCreateReferralButton(siteId: number): Promise<number> {
        return lastValueFrom(this._litRefRepo.getShowCreateReferralButton(siteId));
    }

    async createLitigationReferralBySite(siteId: number): Promise<Core.LitigationReferralCreateResult> {
        return lastValueFrom(this._litRefRepo.createLitigationReferralBySite(siteId));
    }

    async createLitigationReferralByTaskIds(taskIds: number[]): Promise<Core.LitigationReferralCreateResult> {
        return lastValueFrom(this._litRefRepo.createLitigationReferralByTaskIds(taskIds));
    }

    async getLitigationReferral(referralId: number): Promise<Core.LitigationReferralModel> {
        return lastValueFrom(this._litRefRepo.getLitigationReferral(referralId));
    }

    async getLitigationReferralByTaskId(taskId: number): Promise<Core.LitigationReferralModel> {
        return lastValueFrom(this._litRefRepo.getLitigationReferralByTaskId(taskId));
    }

    async getLitigationReferralLiveData(referralId: number): Promise<Core.LitigationReferralModel> {
        return lastValueFrom(this._litRefRepo.getLitigationReferralLiveData(referralId));
    }

    async getLitigationReferralOriginalData(referralId: number): Promise<Core.LitigationReferralModel> {
        return lastValueFrom(this._litRefRepo.getLitigationReferralOriginalData(referralId));
    }

    async getLitigationReferralSiteClasses(): Promise<Core.LitigationReferralSiteClassesModel> {
        return lastValueFrom(this._litRefRepo.getLitigationReferralSiteClasses());
    }

    async getCoCounselFirms(includeCoCounselFirmIds: number[] = []): Promise<Core.CoCounselFirmModel[]> {
        return lastValueFrom(this._litRefRepo.getCoCounselFirms(includeCoCounselFirmIds));
    }

    async updateLitigationReferral(referralModel: Core.LitigationReferralModel): Promise<void> {
        return lastValueFrom(this._litRefRepo.updateLitigationReferral(referralModel));
    }

    async deleteLitigationReferral(referralId: number): Promise<void> {
        return lastValueFrom(this._litRefRepo.deleteLitigationReferral(referralId));
    }

    async getLitigationReferralEligibleAppeals(
        referralId: number,
    ): Promise<Core.LitigationReferralEligibleAppealModel[]> {
        return lastValueFrom(this._litRefRepo.getLitigationReferralEligibleAppeals(referralId));
    }

    async addAppealsToLitigationReferral(referralId: number, appealIds: number[]): Promise<void> {
        return lastValueFrom(this._litRefRepo.addAppealsToLitigationReferral(referralId, appealIds));
    }

    async removeAppealsFromLitigationReferral(referralId: number, appealIds: number[]): Promise<void> {
        return lastValueFrom(this._litRefRepo.removeAppealsFromLitigationReferral(referralId, appealIds));
    }

    async syncAllAppeals(referralId: number): Promise<void> {
        return lastValueFrom(this._litRefRepo.syncAllAppeals(referralId));
    }

    async setPrimaryAppeal(referralId: number, appealId: number): Promise<void> {
        return lastValueFrom(this._litRefRepo.setPrimaryAppeal(referralId, appealId));
    }

    async getReferralDataForEngagement(
        engagementNumberAH: string,
    ): Promise<Core.LitigationReferralEngagementDataModel> {
        return lastValueFrom(this._litRefRepo.getReferralDataForEngagement(engagementNumberAH));
    }

    async areLitigationReferralsAllowedForCompany(tlCompanyId: number): Promise<boolean> {
        return lastValueFrom(this._litRefRepo.areLitigationReferralsAllowedForCompany(tlCompanyId));
    }

    async areLitigationReferralsAllowedForAppeal(appealId: number): Promise<boolean> {
        return lastValueFrom(this._litRefRepo.areLitigationReferralsAllowedForAppeal(appealId));
    }

    async getAppealLitigationReferralStatus(appealId: number): Promise<Core.AppealLitigationReferralStatusModel> {
        return lastValueFrom(this._litRefRepo.getAppealLitigationReferralStatus(appealId));
    }

    async areAnyLitigationReferralsPendingForCompany(tlCompanyId: number): Promise<boolean> {
        return lastValueFrom(this._litRefRepo.areAnyLitigationReferralsPendingForCompany(tlCompanyId));
    }

    async getLitigationReferralWorkFlowStatus(
        litigationReferralId: number,
    ): Promise<Core.LitigationReferralWorkflowStatusModel> {
        return lastValueFrom(this._litRefRepo.getLitigationReferralWorkFlowStatus(litigationReferralId));
    }

    async completeTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Promise<Core.LitigationReferralTaskUpdateResultModel> {
        return lastValueFrom(
            this._litRefRepo.completeLitigationReferralTask(litigationReferralId, litigationReferralTaskUpdateModel),
        );
    }

    async skipTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Promise<Core.LitigationReferralTaskUpdateResultModel> {
        return lastValueFrom(
            this._litRefRepo.skipLitigationReferralTask(litigationReferralId, litigationReferralTaskUpdateModel),
        );
    }

    async rescindTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Promise<Core.LitigationReferralTaskUpdateResultModel> {
        return lastValueFrom(
            this._litRefRepo.rescindLitigationReferralTask(litigationReferralId, litigationReferralTaskUpdateModel),
        );
    }

    async reassignTask(
        litigationReferralId: number,
        litigationReferralTaskUpdateModel: Core.LitigationReferralTaskUpdateModel,
    ): Promise<Core.LitigationReferralTaskUpdateResultModel> {
        return lastValueFrom(
            this._litRefRepo.reassignLitigationReferralTask(litigationReferralId, litigationReferralTaskUpdateModel),
        );
    }

    insertAddToLitigationReferralToTaskSeries(appealId: number): Promise<boolean> {
        return lastValueFrom(this._litRefRepo.insertAddToLitigationReferralToTaskSeries(appealId));
    }

    removeAddFromLitigationReferralFromTaskSeries(appealId: number): Promise<boolean> {
        return lastValueFrom(this._litRefRepo.removeAddFromLitigationReferralFromTaskSeries(appealId));
    }
    async getTopLevelCompanyIdsByAssessmentTaskIds(taskIDs: number[]): Promise<TopLevelCompanyData[]> {
        if (!taskIDs?.length) {
            return [];
        }

        try {
            return await lastValueFrom(this._litRefRepo.getTopLevelCompanyIdsByAssessmentTaskIds(taskIDs));
        } catch {
            return [];
        }
    }
}
